import React from 'react';
import './UIAccountTable.scss';

export interface UIAccountTableColumn {
  /**
   * Ключ для доступу до даних
   */
  key: string;

  /**
   * Лейбл для заголовка колонки
   */
  label: string;

  /**
   * Flex значення для еластичної ширини
   */
  flex: number;

  /**
   * Мінімальна ширина колонки
   */
  minWidth: string;

  /**
   * Додатковий CSS клас для колонки
   */
  className?: string;

  /**
   * Кастомний рендер для комірки
   */
  render?: (value: any, row: Record<string, any>, index: number) => React.ReactNode;

  /**
   * Кастомний рендер для хедера колонки
   */
  headerRender?: () => React.ReactNode;
}

export interface UIAccountTableProps {
  /**
   * Конфігурація колонок
   */
  columns: UIAccountTableColumn[];

  /**
   * Дані для відображення
   */
  data: Record<string, any>[];

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Варіант фону хедера
   */
  headerVariant?: 'light' | 'gray';
}

// Моковые данные для демонстрації
const mockColumns: UIAccountTableColumn[] = [
  { key: 'browser', label: 'BROWSER', flex: 2, minWidth: '100px' },
  { key: 'dateTime', label: 'DATE/TIME', flex: 2, minWidth: '120px' },
  { key: 'device', label: 'DEVICE', flex: 1, minWidth: '60px' },
  { key: 'location', label: 'LOCATION', flex: 1, minWidth: '60px' },
  { key: 'ipAddress', label: 'IP ADDRESS', flex: 1.5, minWidth: '100px' },
];

const mockData = [
  {
    browser: 'Chrome on iPhone',
    dateTime: '2024-12-02 07:50:28',
    device: 'iPhone X',
    location: 'Canada',
    ipAddress: '123.45.678.321'
  },
  {
    browser: 'Chrome on iMacPro',
    dateTime: '2024-12-02 07:50:28',
    device: 'iMacPro',
    location: 'USA',
    ipAddress: '123.45.678.321'
  }
];

const baseClass = 'UIAccountTable';

const UIAccountTable: React.FC<UIAccountTableProps> = ({
                                                         columns = mockColumns,
                                                         data = mockData,
                                                         className = '',
                                                         headerVariant = 'light'
                                                       }) => {
  const tableClasses = [
    baseClass,
    className
  ].filter(Boolean).join(' ');

  const headerClasses = [
    `${baseClass}__header`,
    `${baseClass}__header--${headerVariant}`
  ].filter(Boolean).join(' ');

  return (
    <div className={tableClasses}>
      {/* Table Header */}
      <div className={headerClasses}>
        {columns.map((column) => {
          const headerCellClasses = [
            `${baseClass}__header-cell`,
            column.className && `${baseClass}__header-cell--${column.className}`
          ].filter(Boolean).join(' ');

          return (
            <div
              key={column.key}
              className={headerCellClasses}
              style={{
                flex: column.flex,
                minWidth: column.minWidth
              }}
            >
              {column.headerRender ? column.headerRender() : column.label}
            </div>
          );
        })}
      </div>

      {/* Table Body */}
      <div className={`${baseClass}__body`}>
        {data.map((row, rowIndex) => (
          <div key={rowIndex} className={`${baseClass}__row`}>
            {columns.map((column) => {
              const cellClasses = [
                `${baseClass}__cell`,
                column.className && `${baseClass}__cell--${column.className}`
              ].filter(Boolean).join(' ');

              const cellValue = row[column.key];
              const renderedValue = column.render
                ? column.render(cellValue, row, rowIndex)
                : cellValue;

              return (
                <div
                  key={column.key}
                  className={cellClasses}
                  style={{
                    flex: column.flex,
                    minWidth: column.minWidth
                  }}
                >
                  {renderedValue}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UIAccountTable;